<template>
  <div :class="['header-search', 'body-l-medium', { focus }]">
    <div :class="['line', 'body-l-medium', { focus }]">
      <UiIcon
        class="icon close-window-catalog-search"
        name="arrow-left"
        size="24"
        @click="onBlur"
      />
      <div
        v-show="!loading"
        :class="['search-hint', 'body-l-medium', { show: searchActive && focus && hints.hint }]"
      >
        <span :class="['hide-start', { 'hide-full': !hints.hint }]">{{
          hints.hideHint
        }}</span
        ><span>{{ hints.hint }}</span>
      </div>
      <div
        :class="[
          'mobile-placeholder',
          'body-l-medium',
          { show: !focus && !search },
        ]"
      >
        Найти товары
      </div>
      <form action=".">
        <input
          size="1"
          id="input-search-field"
          ref="input"
          :value="search"
          debounce-events="input"
          type="search"
          placeholder="Найти в 3 865 магазинах"
          :class="[{ focus }, 'body-l-medium']"
          @click="focus = true"
          @input="onInput"
          @keydown.enter.stop.prevent="go"
          @keydown.arrow-up.prevent="selectHint('up')"
          @keydown.arrow-down.prevent="selectHint('down')"
          @keydown.tab.prevent="selectHint('down')"
          @keydown.arrow-right.stop="completeText"
          @keydown.escape="clear(false)"
          autocomplete="off"
        />
      </form>
      <div @click="go" class="header-search-button">
        <UiIcon name="header-search" size="24" stroke="#ffffff" />
      </div>
      <div v-if="!!search" class="close" @click="onBlur">
        <UiIcon
          alt="clear"
          name="close-grey-circle"
          size="24"
          @click.prevent.stop="clearSearchString"
        />
      </div>
      <UiIcon
        class="icon close-window"
        name="close"
        size="20"
        @click="onBlur"
      />
    </div>
    <div
      v-show="showSuggestionResults"
      class="search-result"
      :class="{ withPadding: !showHistory }"
    >
      <div v-if="showSuggest" :class="{'l-line': totalSuggestions.categories.length || totalSuggestions.models.length || totalSuggestions.presets.length}">
        <UiHeaderSearchHelper
          v-if="searchQueries.length"
          :items="totalSuggestions.search_queries"
          @go-search="onSuggests"
        />
      </div>
      <UiHeaderSearchHistory
        v-if="showHistory"
        :history="searchHistory"
        @deleteHistory="deleteHistory"
        @closeHistory="blurVisible"
        @goHistorySearch="goHistorySearch"
      />
      <div class="l-line-block" v-else>
        <UiHeaderSearchWithImage
          title="Категории"
          v-if="totalSuggestions.categories.length"
          :items="totalSuggestions.categories"
          @go-search="onSuggests"
        />
        <UiHeaderSearchWithImage
          v-if="totalSuggestions.models.length"
          :items="totalSuggestions.models"
          @go-search="onSuggests"
        />
        <UiHeaderSearchHelper
          v-if="totalSuggestions.presets.length"
          :items="totalSuggestions.presets"
          @go-search="onSuggests"
        />
      </div>
    </div>
    <div class="bg" @click="onBlur" />
  </div>
</template>

<script lang="ts" setup>
import type { SuggestResponse, SuggestSearchItem } from '@/interfaces/app';
import { useDebounceFn, useScrollLock } from '@vueuse/core';
import { useGlobalStore } from '~/stores/global';
import { SearchMatch, SearchSuggests } from '~/api/search';
import { storeToRefs } from 'pinia';
import escapeStringRegexp from 'escape-string-regexp';
import type { RouteLocationNormalized } from 'vue-router';

const historyStorageName = 'historySearch';
const { $ymAnyEvent } = useNuxtApp();

const route = useRoute();
const router = useRouter();

const globalStore = useGlobalStore();

const emit = defineEmits(['showBottomMenu', 'toggleVisibleCatalog', 'onBlur']);
const input = ref<HTMLElement>();
const search = ref<string>(decodeURI(String(route?.query?.query || '')));
const focus = ref(false);
const hideHint = ref('');
const hint = ref('');
const activeHintIndex = ref(-1);
const displayTurn = ref(false);
const categories = ref<SuggestSearchItem[]>([]);
const searchQueries = ref<SuggestSearchItem[]>([]);
const models = ref<SuggestSearchItem[]>([]);
const presets = ref<SuggestSearchItem[]>([]);
const loading = ref(false);
const html = ref<HTMLElement | null>(null);
const isLocked = useScrollLock(html);
const searchHistory = ref<String[]>([]);
const sendYa = () => {
  $ymAnyEvent('reachGoal', 'click_poisk');
};
const { isInitSearch } = storeToRefs(globalStore);

// push if changed
router.beforeEach(
  (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    clear(to.path === '/search/');
    if (/\/search\//.test(to.path) && to.query?.query) {
      search.value = decodeURI(String(to.query?.query));
    }
  }
);
watch(focus, (value, _) => {
  if (html.value) {
    isLocked.value = value;
  }
  emit('showBottomMenu', !value);
});

onMounted(() => {
  html.value = document.querySelector('html');
  displayTurn.value = true;
  loadHistoryItems();
});
// computed

// all suggestions
const totalSuggestions = computed(() => {
  const categoriesMapped = categories.value.map(
    (category: SuggestSearchItem) => ({
      ...category,
      ...splitString(category?.name),
    })
  );
  const presetsMapped = presets.value.map((preset: SuggestSearchItem) => ({
    ...preset,
    ...splitString(preset.name),
  }));
  const modelsMapped = models.value.map((model: SuggestSearchItem) => ({
    ...model,
    ...splitString(model.name || model.title),
  }));
  const search_queriesMapped = searchQueries.value.map(
    (searchQueries: SuggestSearchItem) => ({
      ...searchQueries,
      ...splitString(searchQueries.name),
    })
  );

  return {
    categories: categoriesMapped,
    presets: presetsMapped,
    models: modelsMapped,
    search_queries: search_queriesMapped,
    all: [
      ...search_queriesMapped,
      ...categoriesMapped,
      ...presetsMapped,
      ...modelsMapped,
    ],
    total:
      searchQueries.value.length +
      categories.value.length +
      presets.value.length +
      models.value.length,
  };
});
const searchActive = computed<Boolean>(() => search.value.length > 0);

const showSuggestionResults = computed<Boolean>(
  () =>
    (searchActive.value && focus.value && totalSuggestions.value.total > 0) ||
    showHistory.value ||
    showSuggest.value
);

const showHistory = computed<Boolean>(
  () =>
    !showSuggest.value &&
    searchHistory.value.length > 0 &&
    search.value.length < 3 &&
    focus.value
);
const showSuggest = computed<Boolean>(
  () => searchQueries.value.length > 0 && search.value.length > 0 && focus.value
);
const getActiveSuggestion = computed<SuggestSearchItem>(() => {
  return totalSuggestions.value.all[
    activeHintIndex.value === -1 ? 0 : activeHintIndex.value
  ];
});

// methods
const replaceSimvol = (str: String) => {
  if (!str) return;
  return str.replaceAll('%', '');
};

const splitString = (str: String) => {
  if (!str) return { data: '' };
  const str1 = escapeStringRegexp(search.value);
  const reg = new RegExp('(' + str1 + ')', 'ig');
  const data = str1?.length
    ? str.replace(reg, '<span class="hint">$1</span>')
    : str;
  return { data };
};

const splitHint = (str: String) => {
  if (!str) return { startString: str, endString: '', searchInString: '' };
  const arr = str
    .toLowerCase()
    .split(decodeURI(replaceSimvol(search.value)).toLowerCase());
  if (arr.length === 1)
    return { startString: str, endString: '', searchInString: '' };
  const getOriginalCase = (subStr: String) => {
    const index = str.toLowerCase().indexOf(subStr.toLowerCase());
    if (index === -1) return '';
    return [...str].splice(index, subStr.length).join('');
  };
  const startString = getOriginalCase(arr.shift() || '');
  const endString = getOriginalCase(arr.join(''));
  const searchInString = getOriginalCase(
    decodeURI(replaceSimvol(search.value))
  );
  return { startString, endString, searchInString };
};

interface HintItem {
  hint: String;
  hideHint: String;
}

const hints = computed<HintItem>(() => {
  return {
    hint: replaceSpacesInString(
      splitHint(getActiveSuggestion.value?.name).endString
    ),
    hideHint: replaceSpacesInString(decodeURI(replaceSimvol(search.value))),
  };
});

const replaceSpacesInString = (v: String) => {
  return v ? v.replace(' ', '\u00A0') : v;
};

// select suggest
const selectHint = (type: string = 'down') => {
  if (type === 'up') {
    activeHintIndex.value =
      activeHintIndex.value === 0
        ? totalSuggestions.value.total - 1
        : activeHintIndex.value - 1;
  }
  if (type === 'down') {
    activeHintIndex.value =
      activeHintIndex.value === totalSuggestions.value.total - 1
        ? 0
        : activeHintIndex.value + 1;
  }
  if (getActiveSuggestion.value?.name) {
    search.value = String(getActiveSuggestion.value.name);
  }
};

// autocomplete input
const completeText = (e: Event) => {
  if (
    hints.value.hint?.length &&
    (<HTMLInputElement>e.target).selectionStart === search.value.length
  ) {
    search.value = `${search.value}${hints.value.hint}`;
  }
};
// clear all interacts
const clear = (keepSearch = false) => {
  input.value?.blur();
  focus.value = false;
  activeHintIndex.value = -1;
  hint.value = '';
  hideHint.value = '';
  searchQueries.value = [];
  clearAllSuggests();
  if (!keepSearch || isInitSearch.value) search.value = '';
  isInitSearch.value = route.path === '/';
};

// check exist route and push if exist
const go = async () => {
  if (!search.value) {
    clear();
    emit('toggleVisibleCatalog', false);
    onBlur();
    return;
  }
  isInitSearch.value = true;
  sendYa();
  if (activeHintIndex.value === -1) {
    await goSearch();
  } else {
    let slug = getActiveSuggestion.value?.slug;
    slug = slug.includes('query') ? `${slug}` : `${slug}/`;
    router.push(slug[0] === '/' ? `${slug}` : `/${slug}`);
    clear();
    emit('toggleVisibleCatalog', false);
    onBlur();
  }
};

const goSearch = async () => {
  try {
    loading.value = true;
    const res = await SearchMatch({
      params: {
        query: decodeURI(replaceSimvol(search.value)),
        region_id: globalStore.region.id,
      },
    });
    if (res?.slug) {
      let slug = res.slug.includes('query') ? `${res.slug}` : `${res.slug}/`;
      router.push(slug[0] === '/' ? `${slug}` : `/${slug}`);
    } else if (
      route.path !== '/search/' ||
      route.query?.query !== decodeURI(replaceSimvol(search.value))
    ) {
      router.push({
        path: '/search/',
        query: { query: decodeURI(replaceSimvol(search.value)) },
      });
    }
    // clear(true);
    emit('toggleVisibleCatalog', false);
    if (replaceSimvol(search.value).localeCompare('') !== 0) {
      addHistory(decodeURI(replaceSimvol(search.value)));
    }
  } catch {
  } finally {
    onBlur();
    loading.value = false;
  }
};

const onSuggests = () => {
  clear();
  sendYa();
  onBlur();
};

const getOnSearchThrottle = useDebounceFn(async () => {
  if (searchActive.value) {
    try {
      activeHintIndex.value = -1;
      const res = await SearchSuggests({
        params: {
          query: decodeURI(replaceSimvol(search.value)),
          region_id: globalStore.region.id,
          limit: 5,
        },
      });
      mapItemsForData(res);
    } catch {
      clearAllSuggests();
    } finally {
      loading.value = false;
    }
  }
}, 500);
const onSearch = () => {
  if (!searchActive.value) return;
  loading.value = true;
  getOnSearchThrottle();
};

// mapper for components structure
const mapItemsForData = (suggests: SuggestResponse) => {
  const {
    categories: c = [],
    models: m = [],
    presets: p = [],
    search_queries: q = [],
  } = suggests;
  categories.value = c;
  presets.value = p;
  searchQueries.value = q;
  models.value = m.map((el: SuggestSearchItem) => ({
    ...el,
    name: el.title || '',
    title: el.name || el.title,
  }));
};

const clearAllSuggests = () => {
  categories.value = [];
  models.value = [];
  presets.value = [];
  searchQueries.value = [];
};

const onInput = (e: Event) => {
  focus.value = true;
  search.value = (<HTMLInputElement>e.target).value;
  onSearch();
};
const onBlur = () => {
  focus.value = false;
  emit('onBlur');
};

// history
const addHistory = (item: String) => {
  let newItems;
  if (searchHistory.value.includes(item)) {
    newItems = [item, ...searchHistory.value.filter((el) => el !== item)];
  } else {
    newItems =
      searchHistory.value.length < 5
        ? [item, ...searchHistory.value]
        : [item, ...searchHistory.value.slice(0, -1)];
  }
  if (window) {
    window.localStorage.setItem(historyStorageName, JSON.stringify(newItems));
  }
  loadHistoryItems();
};

const deleteHistory = (index?: Number) => {
  if (index !== undefined) {
    const newItems = searchHistory.value.filter((_, ind) => ind !== index);
    if (window) {
      window.localStorage.setItem(historyStorageName, JSON.stringify(newItems));
    }
  } else {
    if (window) {
      window.localStorage.removeItem(historyStorageName);
    }
  }
  loadHistoryItems();
};

const loadHistoryItems = () => {
  if (window && window.localStorage?.getItem(historyStorageName)) {
    searchHistory.value = JSON.parse(
      window.localStorage.getItem(historyStorageName) || '{}'
    );
  } else {
    searchHistory.value = [];
  }
};

const blurVisible = (searchValue: string) => {
  input.value?.blur();
  focus.value = false;
  search.value = searchValue;
};

const clearSearchString = () => {
  search.value = '';
};

const goHistorySearch = (to: string) => {
  sendYa();
  onBlur();
  emit('toggleVisibleCatalog');
  router?.push(to);
};
</script>
<style scoped lang="scss" src="./header-search.scss"></style>
